import { useEffect, useState } from "react";
import { Loader2, Scissors, X } from "lucide-react";
import { useResumeSection } from "@/hooks/use-proposals";
import type { Section } from "@/hooks/use-proposals";

interface ResumeSectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  sectionId: string;
  sectionTitle: string;
  currentWordCount: number;
  /**
   * Callback que recibe la sección con el contenido resumido para que el
   * padre lo aplique al editor sin esperar al refetch del detalle.
   */
  onApply: (section: Section) => void;
}

/**
 * Mejora B (changelog cliente): diálogo que resume el contenido existente de
 * una sección a un número objetivo de palabras, anclando el resumen al pliego
 * para no perder requisitos obligatorios.
 *
 * Se solicitó explícitamente en la reunión 2026-04-10 (Rec50) ante la
 * ansiedad de reducir manualmente proyectos de 500 a 200 folios.
 */
export function ResumeSectionDialog({
  isOpen,
  onClose,
  proposalId,
  sectionId,
  sectionTitle,
  currentWordCount,
  onApply,
}: ResumeSectionDialogProps) {
  const [targetWordsInput, setTargetWordsInput] = useState("");
  const [preservePliego, setPreservePliego] = useState(true);
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);

  const resumeMutation = useResumeSection(proposalId);

  // Cuando se abre: sugerimos un objetivo del 70% del contenido actual,
  // redondeado a centenas y dentro del rango aceptado por el backend.
  useEffect(() => {
    if (!isOpen) return;
    const suggested = Math.max(
      50,
      Math.min(10000, Math.round((currentWordCount * 0.7) / 50) * 50)
    );
    if (currentWordCount > 0 && suggested > 0) {
      setTargetWordsInput(String(suggested));
    }
  }, [isOpen, currentWordCount]);

  // Cuando se cierra: reseteamos el formulario para no arrastrar estado
  // entre aperturas distintas del mismo diálogo o entre secciones.
  //
  // Nota: NO incluimos `resumeMutation` en las dependencias aunque usemos
  // su .reset() — useMutation devuelve un objeto nuevo en cada render, y
  // si lo metemos como dep, .reset() actualiza el estado interno de la
  // mutation, lo que provoca un re-render, lo que da un nuevo objeto…
  // bucle infinito ("Maximum update depth exceeded"). Las funciones .reset
  // de React Query son estables incluso si el objeto contenedor no lo es.
  const resetMutation = resumeMutation.reset;
  useEffect(() => {
    if (isOpen) return;
    setTargetWordsInput("");
    setInstructions("");
    setPreservePliego(true);
    setError(null);
    resetMutation();
  }, [isOpen, resetMutation]);

  const handleResume = async () => {
    const trimmed = targetWordsInput.trim();
    const parsed = Number.parseInt(trimmed, 10);
    if (!trimmed || Number.isNaN(parsed) || parsed < 50 || parsed > 10000) {
      setError("Indica un objetivo entre 50 y 10 000 palabras.");
      return;
    }
    if (currentWordCount > 0 && parsed >= currentWordCount) {
      setError(
        `El objetivo (${parsed}) debe ser menor que el contenido actual (${currentWordCount}).`
      );
      return;
    }
    setError(null);

    try {
      const section = await resumeMutation.mutateAsync({
        sectionId,
        target_words: parsed,
        preserve_pliego_requirements: preservePliego,
        instructions: instructions.trim() || undefined,
      });
      onApply(section);
      onClose();
    } catch {
      // El hook muestra el toast; dejamos el diálogo abierto para reintentar.
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Resumir la sección ${sectionTitle}`}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Resumir a N palabras</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
            disabled={resumeMutation.isPending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Sección:{" "}
            <span className="font-medium text-gray-900">{sectionTitle}</span>
            {currentWordCount > 0 && (
              <span className="text-gray-500"> · {currentWordCount} palabras actuales</span>
            )}
          </p>

          <div>
            <label
              htmlFor="resume-target-words"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Longitud objetivo (palabras)
            </label>
            <input
              id="resume-target-words"
              type="number"
              inputMode="numeric"
              min={50}
              max={10000}
              step={50}
              value={targetWordsInput}
              onChange={(e) => {
                setTargetWordsInput(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Ej: 200"
              disabled={resumeMutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
              aria-invalid={!!error}
            />
            {error && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="resume-instructions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Instrucciones adicionales{" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <textarea
              id="resume-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Ej: Mantén la tabla de requisitos, prioriza la metodología..."
              rows={3}
              disabled={resumeMutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none disabled:opacity-50"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={preservePliego}
              onChange={(e) => setPreservePliego(e.target.checked)}
              disabled={resumeMutation.isPending}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              Anclar el resumen al pliego para no perder requisitos obligatorios.
              <span className="block text-xs text-gray-500 mt-0.5">
                Recomendado. Desactívalo solo si la sección no depende del pliego.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={resumeMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleResume}
            disabled={resumeMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {resumeMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Resumiendo...
              </>
            ) : (
              <>
                <Scissors className="w-4 h-4" />
                Resumir
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResumeSectionDialog;
